"use client";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Checkbox, Modal, Switch } from "antd";
import Helper from "../../util/helper";
import formatMsg from "../../util/formatMsg";
import { notification } from "../../util/notification";
import HostedCardSetup, { getPendingCardSetup } from "../HostedCardSetup/page";
import { isRetrying, getCardScope } from "../../util/autopayCardScope";
import {
  getAccountSubscriptions, getPaymentMethods,
  retryAutopay, replaceCard, confirmIncrease, enrollSubscription, sliceOperation,
} from "../../redux/payment/reducer";

// Admin/parent read-side of recurring autopay. Fetches the account's subscriptions + saved cards.
// Two modes:
//   mode="pill"  – a slim header indicator: "● Autopay on · Visa ••••4242 ›"
//   mode="full"  – (default) the "Billing" view: Payment methods section + Autopay subscription
//                  cards. With a bookingId it focuses the one subscription (booking/invoice detail).
//   mode="action" – just the one fix a booking card offers for its bookingId (update / add card,
//                  review amount, set up autopay), drawn by the card's renderButton, plus the same
//                  dialogs as the full view. Renders `fallback` when there is nothing to fix.
// Styling follows recurring-ux.css (--rp-* tokens).

const t = {
  // The centre's theme colour (schoolConfig.themeColor, set as CSS variables at login by
  // Helper.setThemeColorCssVariables), with the default purple as the fallback.
  purple: "var(--primary-color, #7413BD)", purpleSoft: "var(--lightest-theme-color, #F4ECFB)", purpleLine: "var(--lighten-theme-color, #E3CCF5)",
  ink: "#1A1430", muted: "#6B6580", line: "#E7E3EF", bg: "#fff",
  radius: 16, radiusSm: 12, shadow: "0 18px 40px -24px rgba(60,20,110,0.28)",
  ok: "#1F9D6B", okSoft: "#F0FAF5", okLine: "#BFE7D4",
  warn: "#B7791F", warnSoft: "#FBF3E6", warnLine: "#F0D9AE",
  danger: "#D64545", dangerSoft: "#FDECEC", dangerLine: "#F3C8C8",
};

// One height and weight for every card action, so two labels of different lengths don't read as
// two different strengths of choice.
const actBtn: React.CSSProperties = { height: 34, borderRadius: 8, fontWeight: 600, fontSize: 13 };

const fmtDate = (ms?: number | null) => {
  if (!ms) return "—";
  try { return new Date(ms).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const freqLabel = (s: any) => {
  const unit: any = { DAILY: "day", WEEK: "week", MONTH: "month" }[s?.frequency] || String(s?.frequency || "").toLowerCase();
  const n = Number(s?.cadenceValue) || 1;
  if (n > 1) return `Every ${n} ${unit}s`;
  return ({ day: "Daily", week: "Weekly", month: "Monthly" } as any)[unit] || (unit ? unit.charAt(0).toUpperCase() + unit.slice(1) : "Recurring");
};

const CUR_SYMBOL: any = { INR: "₹", USD: "$", GBP: "£", EUR: "€", CAD: "$", AUD: "$", SGD: "$" };
const fmtMoney = (amt?: number | null, cur?: string | null) => {
  if (amt == null || isNaN(Number(amt))) return null;
  const code = String(cur || "").toUpperCase();
  const sym = CUR_SYMBOL[code] || (code ? code + " " : "");
  return `${sym}${Number(amt).toLocaleString()}`;
};

// Titlecase a card brand ("visa" -> "Visa") so it reads consistently everywhere.
const brandLabel = (b?: string | null) => (b ? String(b).charAt(0).toUpperCase() + String(b).slice(1).toLowerCase() : "Card");

// Collapse duplicate saved cards: same physical card = same Stripe fingerprint, or (no fingerprint,
// e.g. test PMs) same brand+last4+expiry. No card outranks another, so the first copy survives.
const dedupCards = (list: any[]) => {
  const seen = new Set<string>();
  return list.filter((c) => {
    const key = c.fingerprint || `${c.brand}|${c.last4}|${c.expMonth}|${c.expYear}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Active but mid-dunning (a charge failed, retry pending, not yet handed to staff). Distinct from
// needsAttention (retries exhausted) — without this a failing sub looks perfectly healthy.

// The cycle a payer is being asked to approve: the EARLIEST hold on the subscription. Several
// cycles can pile up behind one unapproved price change, and consent is taken against the first
// one that breached — approving it covers the rest of the same change.
const earliestHold = (s: any) => {
  const holds = s?.autopayHolds && typeof s.autopayHolds === "object" ? Object.values(s.autopayHolds) : [];
  if (!holds.length) return null;
  return (holds as any[]).filter(Boolean).sort((a, b) => (Number(a.since) || 0) - (Number(b.since) || 0))[0] || null;
};

const statusPill = (s: any) => {
  // Checked BEFORE needsAttention: a consent hold sets that flag too, but "we need your approval"
  // and "we could not collect" are different problems with different fixes, and showing the first
  // as the second sends the payer to change a card that is working perfectly well.
  if (earliestHold(s)) return { label: formatMsg("autopay.approvalNeeded"), bg: t.warnSoft, fg: t.warn, line: t.warnLine };
  if (s?.needsAttention) return { label: formatMsg("autopay.needsAttention"), bg: t.dangerSoft, fg: t.danger, line: t.dangerLine };
  if (isRetrying(s)) return { label: formatMsg("autopay.pastDue"), bg: t.warnSoft, fg: t.warn, line: t.warnLine };
  switch (s?.status) {
    case "active": return { label: formatMsg("autopay.active"), bg: t.okSoft, fg: t.ok, line: t.okLine };
    case "pending_setup": return { label: formatMsg("autopay.pendingCard"), bg: t.warnSoft, fg: t.warn, line: t.warnLine };
    case "paused": return { label: formatMsg("autopay.paused"), bg: "#EFEDF3", fg: t.muted, line: t.line };
    case "cancelled": return { label: formatMsg("autopay.riCancelled"), bg: "#EFEDF3", fg: t.muted, line: t.line };
    case "completed": return { label: formatMsg("autopay.status.completed"), bg: "#EFEDF3", fg: t.muted, line: t.line };
    default: return { label: String(s?.status || ""), bg: "#EFEDF3", fg: t.muted, line: t.line };
  }
};


const Pill = ({ label, bg, fg, line }: any) => (
  <span style={{ background: bg, color: fg, border: `1px solid ${line || bg}`, fontWeight: 600, fontSize: 11.5, padding: "2px 10px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}>{label}</span>
);

const CardChip = ({ card }: any) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff", border: `1px solid ${t.line}`, borderRadius: 10, padding: "7px 12px" }}>
    <span style={{ fontWeight: 700, fontSize: 11, color: t.purple, background: t.purpleSoft, borderRadius: 5, padding: "1px 6px" }}>{String(card.brand || "CARD").toUpperCase()}</span>
    <span style={{ fontWeight: 600, color: t.ink }}>•••• {card.last4 || "----"}</span>
    {card.expMonth ? <span style={{ color: t.muted, fontSize: 12 }}>exp {String(card.expMonth).padStart(2, "0")}/{String(card.expYear || "").slice(-2)}</span> : null}
    {card.expired ? (
      <span style={{ background: t.dangerSoft, color: t.danger, border: `1px solid ${t.dangerLine}`, fontWeight: 700, fontSize: 10, letterSpacing: "0.04em", textTransform: "uppercase", borderRadius: 5, padding: "2px 7px" }}>
        {formatMsg("autopay.expired")}
      </span>
    ) : null}
  </span>
);

const Row = ({ k, v }: any) => (
  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, margin: "5px 0" }}>
    <span style={{ color: t.muted }}>{k}</span>
    <span style={{ fontWeight: 500, textAlign: "right", color: t.ink }}>{v}</span>
  </div>
);

export default function RecurringAutopayInfo({ accountId, bookingId, mode = "full", style, invoice, pricePack, standalone = false, renderButton, fallback = null, allowEnrol = true }: any) {
  const dispatch = useDispatch();
  // Whose cards these are decides who may choose them. A family's card is theirs: staff can chase
  // a failed charge and retry the card already on file, but they never add, replace or switch one
  // on a family's behalf — the same rule that keeps a guest checkout off an account's saved cards.
  // Read from the workspace rather than passed per screen, so a new admin view inherits it instead
  // of having to remember a prop (forgetting one would fail open).
  const canManageCards = process.env.NEXT_PUBLIC_WORKSPACE_NAME !== "admin";
  const { paymentReducer, loggedInData, firebase }: any = Helper.getSelectorState(["paymentReducer"]);
  const authData = loggedInData || firebase;
  const subs: any[] = paymentReducer?.subscriptions || [];
  const cards: any[] = paymentReducer?.paymentMethods || [];

  // Action mode sits on every card of a list, which fetches the subscriptions once for all of them.
  useEffect(() => {
    if (accountId && mode !== "action") {
      dispatch(getAccountSubscriptions({ loggedInData: authData, accountId }));
      dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const relevant = bookingId ? subs.filter((s) => (s.bookingIds || []).includes(bookingId)) : subs;
  const cardFor = (pmId?: string) => cards.find((c) => c.id === pmId);
  const relevantHasNoSub = bookingId
    ? !subs.some((s) => (s.bookingIds || []).includes(bookingId) && s.status !== "cancelled")
    : false;

  // Which enrolment the payer is fixing, and how far the new card should reach. Scope is asked
  // rather than inferred: a grandparent's card on one child's classes looks exactly like an
  // out-of-date one from here, and only the family knows which it is.
  const [fixing, setFixing] = useState<any>(null);
  const [applyToAll, setApplyToAll] = useState(true);
  const [reviewing, setReviewing] = useState<any>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [enrolConsent, setEnrolConsent] = useState(false);
  // Consent for a card added to an enrolment staff set up (the center requires autopay). The family
  // never agreed to recurring charges at checkout, so they agree here — the same tick as Set up autopay.
  const [fixConsent, setFixConsent] = useState(false);

  // Back from Stripe's card page. It returns to a freshly loaded page, so the dialog the payer
  // left from is closed — and the new card was only ever applied from inside it (the card landed
  // in the vault, but the failing subscription kept its old card). Reopen that dialog; its card
  // step picks up the new card and finishes. Every card on a list mounts one of these, so only the
  // one for the booking it was started from resumes; the Autopay tab resumes its own.
  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current || !accountId) return;
    const intent = getPendingCardSetup(accountId)?.intent;
    if (!intent) return;
    if (bookingId ? intent.bookingId !== bookingId : !!intent.bookingId) return;
    if (intent.kind === "enrol") {
      resumedRef.current = true;
      setEnrolConsent(true);   // they ticked it before leaving; the card step only shows once ticked
      setEnrolling(true);
      return;
    }
    const sub = subs.find((x) => x.id === intent.subscriptionId);
    if (!sub) return;          // subscriptions still loading
    resumedRef.current = true;
    setApplyToAll(intent.applyToAll !== false);
    setFixConsent(!!intent.consent);   // ticked before leaving for Stripe's card page
    setFixing(sub);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, bookingId, subs]);

  // A recurring booking the family chose to pay manually has NO subscription, so it never appears
  // on the autopay screen and the "set up autopay" email had nowhere to send them. Offer it here,
  // on the booking itself, where the booking already lives. Parent-side only.
  const canEnrolHere = !!bookingId
    && Helper.isAccount(firebase)
    && relevantHasNoSub
    && pricePack?.paymentType === "RECURRING"
    && ["DAILY", "WEEK", "MONTH", "CUSTOM"].includes(pricePack?.frequency)
    && Helper.isAutopayAvailable(firebase);

  // Consent is not optional politeness: enrollSubscriptionWorker refuses without it.
  const onEnrolCardVaulted = (paymentMethodId: string) => {
    dispatch(enrollSubscription({
      loggedInData: authData,
      accountId,
      bookingIds: [bookingId],
      pricePackId: pricePack?.id,
      paymentMethodId,
      autopayEnrolled: true,
      consent: { agreed: true, termsVersion: "recurring-v1" },
      channel: "parent_portal",
      createdBy: accountId,
    }));
  };

  const uniqueCards = dedupCards(cards);
  // The booking whose button opened the dialog always gets the card; the toggle adds the others
  // that need one. See util/autopayCardScope.
  const { inScope, untouched, otherBroken } = getCardScope(subs, fixing, applyToAll);
  // Any enrolment this card will start that staff set up — the server starts those only with consent.
  const scopeNeedsConsent = inScope.some((s: any) => s.consentPending) || !!fixing?.consentPending;

  const refresh = () => {
    dispatch(getAccountSubscriptions({ loggedInData: authData, accountId }));
    dispatch(getPaymentMethods({ loggedInData: authData, accountId }));
  };

  // The card has landed in the vault — now point it at the enrolments the payer chose and let the
  // backend collect whatever is already due on them.
  const onCardVaulted = (paymentMethodId: string) => {
    dispatch(replaceCard({
      loggedInData: authData, accountId, paymentMethodId,
      // Name exactly the bookings the dialog listed under "Will use it". The backend applies a card
      // to every booking it is named for, but "apply to all" alone only reaches broken ones, which
      // left a healthy booking's "Change card" (with the toggle on) still on its old card.
      applyToAll, subscriptionIds: inScope.map((s) => s.id),
      consent: scopeNeedsConsent && fixConsent ? { agreed: true, termsVersion: "recurring-v1" } : undefined,
    }));
  };

  useEffect(() => {
    // Every card on a list mounts one of these; only the one whose dialog ran the operation may
    // consume it, or each card would toast and refetch once per card.
    if (mode === "action" && !fixing && !reviewing && !enrolling) return;
    const op = paymentReducer?.operationType;
    if (op === "CARD_REPLACED") {
      const r = paymentReducer?.replaceCardResult;
      setFixing(null);
      setFixConsent(false);
      notification("success", r?.charged
        ? formatMsg("autopay.cardAppliedAndPaid")
        : formatMsg("autopay.cardApplied"));
      dispatch(sliceOperation({ operationType: undefined }));
      refresh();
    }
    if (op === "ENROLLED") {
      setEnrolling(false);
      setEnrolConsent(false);
      notification("success", formatMsg("autopay.turnedOn"));
      dispatch(sliceOperation({ operationType: undefined }));
      refresh();
    }
    if (op === "INCREASE_CONFIRMED") {
      setReviewing(null);
      notification("success", formatMsg("autopay.increaseApproved"));
      dispatch(sliceOperation({ operationType: undefined }));
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentReducer?.operationType]);

  // ── PILL mode: slim header indicator ──────────────────────────────────────
  if (mode === "pill") {
    const active = relevant.filter((s) => s.status !== "cancelled");
    if (!accountId || !active.length) return null;
    const anyAttn = active.some((s) => s.needsAttention);
    const anyRetrying = active.some((s) => isRetrying(s));
    const anyPending = active.some((s) => s.status === "pending_setup");
    // Name a card only when a live subscription is actually pinned to one. With no default to
    // fall back on, showing cards[0] would state a link the system does not make.
    const pinnedId = active.map((s: any) => s.paymentMethodId).find(Boolean);
    const card = pinnedId ? cards.find((c) => c.id === pinnedId) : null;
    // Every enrolment here has run its course: say so rather than "autopay on" for a finished booking.
    const allCompleted = active.every((s) => s.status === "completed");
    const label = anyAttn ? formatMsg("autopay.needsAttention") : anyRetrying ? formatMsg("autopay.pastDue") : anyPending ? formatMsg("autopay.pendingCard")
      : allCompleted ? formatMsg("autopay.status.completed") : formatMsg("autopay.on");
    const dot = anyAttn ? t.danger : (anyRetrying || anyPending) ? t.warn : allCompleted ? t.muted : t.ok;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, background: t.purpleSoft, border: `1px solid ${t.purpleLine}`, borderRadius: 999, padding: "5px 12px" }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: dot }} />
        <span style={{ fontWeight: 600, color: t.purple, fontSize: 12.5 }}>{formatMsg("autopay") + " " + label.toLowerCase()}</span>
        {card ? <span style={{ color: t.muted, fontSize: 12.5 }}>· {brandLabel(card.brand)} •••• {card.last4}</span> : null}
      </span>
    );
  }

  // ── FULL mode: the Billing view ───────────────────────────────────────────
  if (!accountId) return mode === "action" ? fallback : null;
  // Account view: only surface billing once autopay has actually been configured — i.e. at least
  // one subscription exists (active / pending / paused / cancelled). Saved cards alone (e.g. from a
  // one-off payment) do NOT count, so the whole block stays hidden when there are no subscriptions.
  // Rendered as a whole screen (the parent portal's autopay section) there is nothing else on the
  // page, so returning null leaves the payer looking at blank space with no idea whether autopay
  // is off or the page is broken. Embedded in a profile it still hides, as before.
  const nothingToShow = (
    <section style={{ background: t.bg, border: `1px solid ${t.line}`, borderRadius: t.radius, padding: "28px 20px", textAlign: "center", ...(style || {}) }}>
      <div style={{ fontSize: 14.5, fontWeight: 600, color: t.ink, marginBottom: 6 }}>{formatMsg("autopay.noneTitle")}</div>
      <div style={{ fontSize: 13, lineHeight: 1.55, color: t.muted }}>{formatMsg("autopay.noneBody")}</div>
    </section>
  );
  if (mode !== "action" && !bookingId && relevant.length === 0) return standalone ? nothingToShow : null;
  const showMethods = !bookingId && cards.length > 0;
  // canEnrolHere is the one case with no subscription to render but something to offer.
  if (mode !== "action" && !relevant.length && !showMethods && !canEnrolHere) return standalone ? nothingToShow : null;

  const SubCard = ({ s }: any) => {
    const p = statusPill(s);
    const card = cardFor(s.paymentMethodId);
    // On an invoice's detail, show THIS invoice's amount + its due (charge) date instead of the
    // subscription-level amount / next-charge — those are identical for every invoice of the sub,
    // which is why every invoice previously showed the first invoice's figures.
    // A postpaid plan has nothing to pay at booking, so the booking total reads $0. Describe what
    // was agreed instead: the fee per session (up to the weekly maximum) or the fixed fee per period.
    const pk = s.pricePack;
    const postpaidPlan = !invoice && pk?.paymentTiming === "AFTER" && pk?.feeAmount != null;
    const cadence = postpaidPlan
      ? (pk.frequency === "CUSTOM" ? Helper.getBillEveryLabel(pk)
        : Helper.getBillEveryLabel({ billEveryValue: 1, billEveryUnit: pk.frequency === "DAILY" ? "DAYS" : pk.frequency === "WEEK" ? "WEEKS" : "MONTHS" }))
      : "";
    const money = invoice ? fmtMoney((invoice.total ?? invoice.pending ?? s.amount), s.currency)
      : postpaidPlan
        ? (pk.feeBasis === "PER_SESSION" && s.agreedPerCycleAmount != null
          ? formatMsg("autopay.amountPerSessionUpTo", { fee: fmtMoney(pk.feeAmount, s.currency), max: fmtMoney(s.agreedPerCycleAmount, s.currency), cadence })
          : formatMsg("bookingSummary.perPeriod", { amount: fmtMoney(pk.feeAmount, s.currency), cadence }))
        : fmtMoney(s.amount, s.currency);
    // BOOK-520: on an invoice's detail, the charge pass/fail status must reflect THIS invoice's own
    // dunning (retryStates keyed by invoiceId) — not the subscription-level retryState mirror, which
    // tracks the most-recently-touched invoice and so made every invoice show the booking's LAST
    // charge attempt. A paid / never-failed invoice has no entry → no "charge failed" note.
    const invRetry = invoice ? (s.retryStates?.[invoice.id] || null) : null;
    const retrying = invoice
      ? ((Number(invRetry?.failCount) || 0) > 0 && s.status === "active" && !!invRetry?.nextRetryAt)
      : isRetrying(s);
    const retryNextAt = invoice ? invRetry?.nextRetryAt : s.retryState?.nextRetryAt;
    // Waiting on the family for something: a card, a replacement, or an approval.
    const needsSomething = !!earliestHold(s) || s.needsAttention || retrying || s.status === "pending_setup";
    return (
      <div style={{
        // Border and edge stripe only. A tinted ground across a grid this dense turned the whole
        // page into a wash of pink and made the pills harder to read, not easier.
        border: `1px solid ${needsSomething ? p.line : t.line}`,
        boxShadow: needsSomething ? `inset 3px 0 0 ${p.fg}` : "none",
        borderRadius: t.radiusSm,
        padding: "14px 16px",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, gap: 8 }}>
          {/* The booking number is what identifies the row; the pack name repeated across every
              card of the same pack told the reader nothing and crowded out the status. */}
          <span style={{ fontWeight: 700, color: t.ink }}>
            {s.bookingNumber ? `#${s.bookingNumber}` : formatMsg("autopay.subscriptionLabel")}
          </span>
          <Pill label={p.label} bg={p.bg} fg={p.fg} line={p.line} />
        </div>
        {money ? <Row k={formatMsg("autopay.amount")} v={money} /> : null}
        <Row k={formatMsg("autopay.frequency")} v={freqLabel(s)} />
        {invoice
          ? <Row k={formatMsg("autopay.chargeDate")} v={fmtDate(invoice.dueDate)} />
          // nextChargeDate is the soonest day autopay will actually charge. With nothing coming, say
          // why when it matters — every retry on an unpaid invoice failed — and otherwise hide the row.
          : s.nextChargeDate ? <Row k={formatMsg("autopay.riNextCharge")} v={fmtDate(s.nextChargeDate)} />
            : s.retriesExhausted ? <Row k={formatMsg("autopay.riNextCharge")} v={<span style={{ color: t.danger }}>{formatMsg("autopay.retriesExhausted")}</span>} />
              : null}
        {s.studentName ? <Row k={formatMsg("autopay.student")} v={s.studentName} /> : null}
        {s.startDate ? <Row k={formatMsg("autopay.startedOn")} v={fmtDate(s.startDate)} /> : null}
        <Row k={formatMsg("autopay.cardOnFile")} v={card ? `${brandLabel(card.brand)} •••• ${card.last4}${card.expMonth ? ` · exp ${String(card.expMonth).padStart(2, "0")}/${String(card.expYear || "").slice(-2)}` : ""}` : (s.status === "pending_setup" ? formatMsg("autopay.awaitingCard") : "—")} />
        {s?.consent?.acceptedAt ? <Row k={formatMsg("autopay.consentedOn")} v={fmtDate(s.consent.acceptedAt)} /> : null}
        {retrying ? (
          <div style={{ marginTop: 8, fontSize: 12, fontWeight: 500, color: t.warn, background: t.warnSoft, border: `1px solid ${t.warnLine}`, borderRadius: 8, padding: "6px 10px" }}>
            {formatMsg("autopay.retryNote")}{retryNextAt ? ` ${fmtDate(retryNextAt)}` : ""}
          </div>
        ) : null}
        {subActions(s)}
      </div>
    );
  };

  // One action per state, and nothing louder than the state deserves. The screen used to name every
  // one of these conditions and offer no way out of any of them.
  const subActions = (s: any) => {
    if (s.status === "cancelled" || s.status === "paused" || s.status === "completed") return null;
    const hold = earliestHold(s);
    const acts: any[] = [];

    if (hold) {
      acts.push(<Button key="rev" type="primary" block style={actBtn} onClick={() => setReviewing({ s, hold })}>{formatMsg("autopay.reviewAmount")}</Button>);
    } else if (s.status === "pending_setup") {
      if (canManageCards) acts.push(<Button key="add" type="primary" block style={actBtn} onClick={() => { setApplyToAll(true); setFixing(s); }}>{formatMsg("autopay.addCard")}</Button>);
    } else if (s.needsAttention || isRetrying(s)) {
      if (canManageCards) acts.push(<Button key="rep" type="primary" block style={actBtn} onClick={() => { setApplyToAll(true); setFixing(s); }}>{formatMsg("autopay.replaceCard")}</Button>);
      // Retry is only offered where a card is actually on file — otherwise it can only fail again.
      if (s.paymentMethodId) {
        acts.push(<Button key="try" block style={actBtn} onClick={() => dispatch(retryAutopay({ loggedInData: authData, subscriptionId: s.id }))}>{formatMsg("autopay.retryNow")}</Button>);
      }
    } else if (canManageCards) {
      acts.push(<Button key="chg" type="link" size="small" style={{ padding: 0 }} onClick={() => { setApplyToAll(false); setFixing(s); }}>{formatMsg("autopay.changeCard")}</Button>);
    }
    if (!acts.length) return null;

    // Equal widths, one height: two actions of different label lengths otherwise render as two
    // differently-sized buttons and read as different weights of choice, which they are not.
    return (
      <div style={{ display: "flex", gap: 8, marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${t.line}` }}>
        {acts.map((a, i) => <div key={i} style={{ flex: acts.length > 1 ? 1 : "0 0 auto", display: "flex" }}>{a}</div>)}
      </div>
    );
  };

  // Card capture happens on Stripe's hosted page; the toggle above it decides how far the card
  // reaches once it lands. Amounts are deliberately absent — each booking already shows its own on
  // the card behind this dialog, and repeating them here adds noise, not consent.
  const renderReplaceCardModal = () => {
    if (!fixing) return null;
    const isAdd = fixing.status === "pending_setup";
    const ids = (list: any[], dim?: boolean) => (
      <span style={{ display: "inline-flex", gap: 5, flexWrap: "wrap" }}>
        {list.length
          ? list.map((s: any) => (
            <span key={s.id} style={{ fontSize: 13, fontWeight: 600, padding: "3px 9px", borderRadius: 6, border: `1px solid ${dim ? t.line : t.purpleLine}`, color: dim ? t.muted : t.purple, background: dim ? "#fff" : t.purpleSoft }}>
              {s.bookingNumber ? `#${s.bookingNumber}` : s.id}
            </span>
          ))
          : <span style={{ color: t.muted, fontSize: 13.5 }}>—</span>}
      </span>
    );
    return (
      <Modal open onCancel={() => { setFixing(null); setFixConsent(false); }} footer={null} destroyOnClose width={620}
        title={<span style={{ fontSize: 19, fontWeight: 700, color: t.ink }}>
          {`${isAdd ? formatMsg("autopay.addCard") : formatMsg("autopay.replaceCard")} · ${fixing.bookingNumber ? "#" + fixing.bookingNumber : ""}`}
        </span>}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "#FAFAFA", border: `1px dashed ${t.line}`, borderRadius: 8, padding: "13px 14px", fontSize: 14, lineHeight: 1.5, color: t.muted }}>
            {formatMsg("autopay.hostedCardNote")}
          </div>

          {otherBroken.length > 0 && (
            <div style={{ border: `1px solid ${t.purpleLine}`, background: t.purpleSoft, borderRadius: 10, padding: 13 }}>
              <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
                <Switch checked={applyToAll} onChange={setApplyToAll} size="small" style={{ marginTop: 3 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 15.5, color: t.ink }}>{formatMsg("autopay.applyToAllLabel")}</div>
                  <div style={{ fontSize: 13.5, color: t.muted, marginTop: 4, lineHeight: 1.45 }}>{formatMsg("autopay.applyToAllHint")}</div>
                </div>
              </div>
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${t.purpleLine}`, display: "flex", flexDirection: "column", gap: 7 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: t.muted }}>{formatMsg("autopay.willUseIt")}</span>
                  {ids(inScope)}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: t.muted }}>{formatMsg("autopay.unchanged")}</span>
                  {ids(untouched, true)}
                </div>
              </div>
            </div>
          )}

          {/* Staff set this enrolment up, so the family agrees to recurring charges here, as they
              would have at checkout. The card step only appears once they have. */}
          {scopeNeedsConsent && (
            <Checkbox checked={fixConsent} onChange={(e) => setFixConsent(e.target.checked)} style={{ fontSize: 13.5 }}>
              {formatMsg("autopay.consent")}
            </Checkbox>
          )}
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: t.muted }}>{formatMsg("autopay.dueWillBeTaken")}</div>
          {(!scopeNeedsConsent || fixConsent) && (
            <HostedCardSetup accountId={accountId} onSaved={onCardVaulted} size="middle"
              bindTo={{ subscriptionIds: inScope.map((s: any) => s.id) }}
              resumeIntent={{ kind: "replace", subscriptionId: fixing.id, bookingId: bookingId || null, applyToAll, consent: scopeNeedsConsent && fixConsent }} />
          )}
        </div>
      </Modal>
    );
  };

  // The one place an amount belongs: autopay refused to collect precisely because this cycle costs
  // more than the payer agreed to, so the figure is the thing being consented to.
  const renderReviewIncreaseModal = () => {
    if (!reviewing) return null;
    const { s, hold } = reviewing;
    return (
      <Modal open onCancel={() => setReviewing(null)} footer={null} destroyOnClose
        title={`${formatMsg("autopay.reviewAmount")} · ${s.bookingNumber ? "#" + s.bookingNumber : ""}`}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ border: `1px solid ${t.line}`, borderRadius: 10, padding: "12px 13px" }}>
            <Row k={formatMsg("autopay.youApproved")} v={fmtMoney(hold.mandate, s.currency) || "—"} />
            <Row k={formatMsg("autopay.thisCycle")} v={fmtMoney(hold.amount, s.currency) || "—"} />
            {hold.since ? <Row k={formatMsg("autopay.heldSince")} v={fmtDate(hold.since)} /> : null}
          </div>
          <div style={{ fontSize: 13, color: t.muted }}>{formatMsg("autopay.higherThanApprovedNote")}</div>
          <div style={{ border: `1px solid ${t.warnLine}`, background: t.warnSoft, borderRadius: 10, padding: 13 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5, color: t.ink }}>
              {formatMsg("autopay.approvingSets")} {fmtMoney(hold.amount, s.currency)}
            </div>
            <div style={{ fontSize: 12.5, color: t.muted, marginTop: 3 }}>{formatMsg("autopay.approvingScope")}</div>
          </div>
          <div style={{ fontSize: 12.5, color: t.muted }}>{formatMsg("autopay.dueWillBeTaken")}</div>
          {/* One approval per press: busy until the answer is back, so a second press can't send it again. */}
          <Button type="primary" block loading={!!paymentReducer?.isLoading} disabled={!!paymentReducer?.isLoading}
            onClick={() => dispatch(confirmIncrease({ loggedInData: authData, subscriptionId: s.id, invoiceId: hold.invoiceId, confirmedBy: accountId }))}>
            {formatMsg("autopay.approveAndPay")}
          </Button>
          <Button block onClick={() => setReviewing(null)}>{formatMsg("autopay.notNow")}</Button>
        </div>
      </Modal>
    );
  };

  const renderEnrolModal = () => {
    if (!enrolling) return null;
    return (
      <Modal open onCancel={() => setEnrolling(false)} footer={null} destroyOnClose title={formatMsg("autopay.setUpAutopay")}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: "#FAFAFA", border: `1px dashed ${t.line}`, borderRadius: 8, padding: "11px 12px", fontSize: 12.5, color: t.muted }}>
            {formatMsg("autopay.hostedCardNote")}
          </div>
          <Checkbox checked={enrolConsent} onChange={(e) => setEnrolConsent(e.target.checked)} style={{ fontSize: 13 }}>
            {formatMsg("autopay.consent")}
          </Checkbox>
          {/* Switching on autopay settles whatever is already due on this booking, so say so
              before the card page opens rather than after the money has moved. */}
          <div style={{ fontSize: 12.5, color: t.muted }}>{formatMsg("autopay.dueWillBeTaken")}</div>
          {!canManageCards
            ? <div style={{ fontSize: 12.5, color: t.muted }}>{formatMsg("autopay.cardByParentOnly")}</div>
            : enrolConsent
              ? <HostedCardSetup accountId={accountId} onSaved={onEnrolCardVaulted} resumeIntent={{ kind: "enrol", bookingId }}
                // The enrolment is created for this booking once the card lands; bind nothing else.
                bindTo={{ subscriptionIds: [] }} />
              : <div style={{ fontSize: 12.5, color: t.warn }}>{formatMsg("autopay.consentRequiredFirst")}</div>}
        </div>
      </Modal>
    );
  };

  // ── ACTION mode: the card-level fix ──────────────────────────────────────
  // Same precedence as subActions: an approval hold first (the card is fine), then a missing
  // card, then a failing one. Paused / completed enrolments have nothing to fix.
  if (mode === "action") {
    // Until this account's list has landed, every booking looks un-enrolled and would offer
    // "Set up autopay" for a moment.
    if (paymentReducer?.subscriptionsAccountId !== accountId) return fallback;
    const s = relevant.find((x) => x.status !== "cancelled");
    let label: string | null = null;
    let onClick: (() => void) | null = null;
    if (s && s.status !== "paused" && s.status !== "completed") {
      const hold = earliestHold(s);
      if (hold) {
        label = formatMsg("autopay.reviewAmountShort");
        onClick = () => setReviewing({ s, hold });
      } else if (canManageCards && s.status === "pending_setup") {
        label = formatMsg("autopay.addCard");
        onClick = () => { setApplyToAll(true); setFixing(s); };
      } else if (canManageCards && (s.needsAttention || isRetrying(s))) {
        label = formatMsg("autopay.updateCard");
        onClick = () => { setApplyToAll(true); setFixing(s); };
      }
    } else if (!s && allowEnrol && canEnrolHere) {
      label = formatMsg("autopay.setUpAutopay");
      onClick = () => { setEnrolConsent(false); setEnrolling(true); };
    }
    // A dialog reopened after the card page stays up even if this booking no longer needs a fix.
    const dialogOpen = !!(fixing || reviewing || enrolling);
    if ((!label || !onClick) && !dialogOpen) return fallback;
    return (
      <>
        {label && onClick
          ? (renderButton ? renderButton(label, onClick) : <Button block style={actBtn} onClick={onClick}>{label}</Button>)
          : fallback}
        {renderReplaceCardModal()}
        {renderReviewIncreaseModal()}
        {renderEnrolModal()}
      </>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18, ...(style || {}) }}>
      {showMethods && (
        <section style={{ background: t.bg, border: `1px solid ${t.line}`, borderRadius: t.radius, padding: "18px 20px" }}>
          {/* A plain list of what is saved — nothing is ranked and nothing is preselected. Which
              card pays is decided per booking, inside that booking's own Stripe session, so there
              is no list-wide choice to make here and no control that would imply one. */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: t.ink }}>{formatMsg("autopay.savedCards")}</span>
          </div>
          {/* Removing a card is deliberately not offered here: a card is bound to live enrolments,
              and deleting one from a row of chips gives no sense of what stops being paid.
              Changing which card pays is what this screen is for. */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {uniqueCards.map((cd, i) => <CardChip key={cd.id || i} card={cd} />)}
          </div>
        </section>
      )}
      {canEnrolHere && (
        <section style={{ background: t.bg, border: `1px solid ${t.purpleLine}`, borderRadius: t.radius, padding: "16px 18px" }}>
          <div style={{ fontWeight: 700, color: t.ink, marginBottom: 4 }}>{formatMsg("autopay.notOnAutopayTitle")}</div>
          <div style={{ fontSize: 13, color: t.muted, marginBottom: 12 }}>{formatMsg("autopay.notOnAutopayBody")}</div>
          <Button type="primary" style={actBtn} onClick={() => { setEnrolConsent(false); setEnrolling(true); }}>
            {formatMsg("autopay.setUpAutopay")}
          </Button>
        </section>
      )}

      {renderEnrolModal()}

      {renderReplaceCardModal()}
      {renderReviewIncreaseModal()}
      {relevant.length > 0 && (
        <section style={{ background: bookingId ? "transparent" : t.bg, border: bookingId ? "none" : `1px solid ${t.line}`, borderRadius: t.radius, padding: bookingId ? 0 : "18px 20px" }}>
          {!bookingId && <div style={{ fontWeight: 700, color: t.ink, marginBottom: 12 }}>{formatMsg("autopay.subscriptions")} <span style={{ color: t.muted, fontWeight: 500 }}>· {relevant.length}</span></div>}
          <div style={{ display: "grid", gridTemplateColumns: bookingId ? "1fr" : "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 14 }}>
            {relevant.map((s, i) => <SubCard key={s.id || i} s={s} />)}
          </div>
        </section>
      )}
    </div>
  );
}
